import React, { useMemo, useRef, useState } from 'react';

import { useClassName, useWindowSize } from 'common/hooks';
import { Pagination } from 'public/components';
import { ControlledTabs } from 'public/components/Tabs';
import { scrollTo } from 'utils/helpers/scroll';
import { CTA_TYPES, STANDARD_EVENTS } from 'utils/analytics/trackV2';
import { StickyDropDown } from 'public/components/StickyDropdown';

import Center from '../Center';
import Card from '../Card';
import useCustomStyle from '../../hooks';
import Link from '../Link';
import { appendStylesByPrefix, getStylesByPrefix } from '../../utils';

import './list.less';

function ListWithTabs({ component, refs, venue, handleTrack }) {
  const classNames = useClassName('CLPList');
  const [tagIndex, setTagIndex] = useState(0);
  const [page, setPage] = useState(1);
  const ref = useRef();
  const { isMobile } = useWindowSize();
  const [setRef, style] = useCustomStyle(component.fields.settings);
  const pageSize = style['--page-size'] ? parseInt(style['--page-size']) : 1;
  const arrowTabInverted = Boolean(style['--arrow-tab-inverted']);
  const paginationColorInverted = style['--pagination-color-inverted']
    ? true
    : false;

  const type = component.fields.type || 'default';

  const tags = [
    ...new Set(component.fields.items.map((a) => a.fields.tags).flat()),
  ].sort(function (a, b) {
    if (!component?.fields?.tagOrder?.length) return a.localeCompare(b);

    return (
      component.fields.tagOrder.indexOf(a) -
      component.fields.tagOrder.indexOf(b)
    );
  });
  const items = component.fields.items.filter((item) =>
    item.fields?.tags?.includes(tags[tagIndex])
  );
  const pagedItems = items.filter((item, index) => {
    const indexMin = (page - 1) * pageSize;
    const indexMax = indexMin + pageSize;
    return index >= indexMin && index < indexMax;
  });
  const totalPages = Math.ceil(items.length / pageSize);
  const mobileSticky = component.fields.mobileSticky;
  const tabsVariant = style['--tabs-variant'];
  const useEventCards = tabsVariant === 'event-cards';
  const tabItems = tags.map((tag) => {
    const item = component.fields.items.find((candidate) =>
      candidate.fields?.tags?.includes(tag)
    );

    return {
      tag,
      description: item?.fields?.tabDescription,
      icon: item?.fields?.icon,
    };
  });

  return (
    <div
      className={useEventCards ? classNames('event-cards') : undefined}
      ref={(node) => {
        ref.current = node;
        setRef(node);
      }}>
      {isMobile && mobileSticky ? (
        <StickyDropDown
          options={tags}
          selectedIndex={tagIndex}
          parentRef={ref}
          handleChange={(tagName) => {
            setTagIndex(tags.findIndex((tag) => tag === tagName));
            setPage(1);
          }}
        />
      ) : (
        <Center>
          <ControlledTabs
            names={tags}
            selectedIndex={tagIndex}
            styles={{
              container: useEventCards
                ? classNames(['tabs', 'tabs-event-cards'])
                : classNames('tabs'),
            }}
            tabClassName={useEventCards ? classNames('event-tab') : undefined}
            renderTab={
              useEventCards
                ? (tagName, index) => {
                    const tab = tabItems[index];

                    return (
                      <>
                        {tab?.icon?.fields?.file?.url && (
                          <img
                            src={tab.icon.fields.file.url}
                            alt=""
                            aria-hidden="true"
                            className={classNames('event-tab-icon')}
                          />
                        )}
                        <span className={classNames('event-tab-title')}>
                          {tagName}
                        </span>
                        {tab?.description && (
                          <span className={classNames('event-tab-description')}>
                            {tab.description}
                          </span>
                        )}
                      </>
                    );
                  }
                : undefined
            }
            tabStyles={Object.fromEntries(
              Object.entries(style)
                .filter(([key]) => key.startsWith('--tab'))
                .map(([key, value]) => [key.replace('--tab-', ''), value])
            )}
            tabsContainerStyles={Object.fromEntries(
              Object.entries(style)
                .filter(([key]) => key.startsWith('--tabs-container'))
                .map(([key, value]) => [
                  key.replace('--tabs-container-', ''),
                  value,
                ])
            )}
            arrowTabInverted={arrowTabInverted}
            onClick={(tagName) => {
              setTagIndex(tags.findIndex((tag) => tag === tagName));
              setPage(1);
            }}
            handleTrack={handleTrack}
          />
        </Center>
      )}

      <Center
        className={classNames([
          'container',
          style['--layout'] === 'flex' ? 'container-flex' : `container-${type}`,
        ])}
        ref={setRef}
        style={{
          justifyContent: style['--justify-content'] || undefined,
        }}>
        {pagedItems.map((item) => (
          <Card
            key={item.sys.id}
            item={appendStylesByPrefix(component, item, '--card-')}
            refs={refs}
            venue={venue}
            handleTrack={handleTrack}
          />
        ))}
      </Center>
      {totalPages > 1 && (
        <Center>
          <Pagination
            totalPages={Math.ceil(items.length / pageSize)}
            activePage={page}
            onPageChange={(e, data) => {
              setPage(data.activePage);

              scrollTo(ref.current, -138);
            }}
            className={classNames([
              'pagination',
              `pagination-color-${
                paginationColorInverted ? 'inverted' : 'default'
              }`,
            ])}
          />
        </Center>
      )}
    </div>
  );
}

function ListWithoutTabs({ component, refs, venue, handleTrack }) {
  const classNames = useClassName('CLPList');
  const [setRef, style] = useCustomStyle(component.fields.settings);
  const pageSize = style['--page-size'] ? parseInt(style['--page-size']) : 1;
  const borderRadius = style['--border-radius'];
  const transition = style['--transition'];
  const transform = style['--transform'];
  const order = style['--order'] || undefined;
  const sortAlphabetically = style['--alphabetical-sort'];
  const itemsStyles = {
    marginBottom: style['--margin-bottom'] || 'auto',
    marginTop: style['--margin-top'] || 'auto',
    order,
  };

  const [page, setPage] = useState(1);

  const processedItems = useMemo(() => {
    if (!sortAlphabetically) {
      return component.fields.items;
    }

    const result = [...component.fields.items];
    const firstPage = result.slice(0, pageSize);
    const rest = result
      .slice(pageSize)
      .sort((a, b) => a.fields.name.localeCompare(b.fields.name));

    return [...firstPage, ...rest];
  }, [component.fields.items, pageSize, sortAlphabetically]);

  const pagedItems = processedItems.slice(0, page * pageSize);

  const type = component.fields.type || 'default';
  const containerInnerCenter = style['--container-inner-center'] === 'true';
  const containerInnerBottom = style['--container-inner-bottom'];
  const containerInnerWidth = style['--container-inner-width'];
  const containerInnerBorderRadius = style['--container-inner-border-radius'];
  const containerInnerBackgroundColor =
    style['--container-inner-background-color'] || 'white';
  const containerInnerPadding = style['--container-inner-padding'];
  const containerInnerMaxWidth = style['--container-inner-max-width'];
  const wrapperStyles = getStylesByPrefix('--wrapper-', style);

  const containerStyles = !containerInnerCenter
    ? {}
    : {
        position: 'absolute',
        left: '50%',
        transform: 'translateX(-50%)',
        bottom: containerInnerBottom,
        width: containerInnerWidth,
        borderRadius: containerInnerBorderRadius,
        backgroundColor: containerInnerBackgroundColor,
        padding: containerInnerPadding,
        maxWidth: containerInnerMaxWidth,
      };

  return (
    <>
      <Center
        style={{
          ...wrapperStyles,
          ...containerStyles,
          justifyContent: style['--justify-content'] || undefined,
          ...itemsStyles,
        }}
        className={classNames([
          'container',
          style['--layout'] === 'flex' ? 'container-flex' : `container-${type}`,
        ])}
        ref={setRef}>
        {pagedItems.map((item) => {
          if (item.sys.contentType.sys.id === 'lpLink') {
            return (
              <Link
                key={item.sys.id}
                component={appendStylesByPrefix(component, item, '--link-')}
                refs={refs}
                handleTrack={handleTrack}
              />
            );
          }

          return (
            <Card
              key={item.sys.id}
              item={appendStylesByPrefix(component, item, '--card-')}
              refs={refs}
              venue={venue}
              handleTrack={handleTrack}
            />
          );
        })}
      </Center>
      {pagedItems.length < processedItems.length && (
        <Center className={classNames('explore-more')}>
          <button
            className={classNames('explore-more')}
            style={{ borderRadius, transition, transform }}
            onClick={() => {
              if (handleTrack) {
                handleTrack({
                  event: STANDARD_EVENTS.click,
                  params: {
                    label: 'Explore More',
                    cta_type: CTA_TYPES.showMore,
                  },
                });
              }
              setPage((page) => page + 1);
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.backgroundColor = style['--hover-bg'])
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.backgroundColor = 'black')
            }>
            Explore More
          </button>
        </Center>
      )}
    </>
  );
}

export default function List({ component, refs, venue, handleTrack }) {
  if (component.fields.groupTags) {
    return (
      <ListWithTabs
        component={component}
        refs={refs}
        venue={venue}
        handleTrack={handleTrack}
      />
    );
  }

  return (
    <ListWithoutTabs
      component={component}
      refs={refs}
      venue={venue}
      handleTrack={handleTrack}
    />
  );
}
